<template>
  <div class="module-app" :class="{ 'avatar-popup-root': isAvatarPopup }">
    <AvatarPopupShell v-if="isAvatarPopup" />

    <template v-else>
      <AuthManager v-if="!isAuthenticated" @auth-success="handleAuthSuccess" />

      <ConfigManager
        v-else-if="isAuthenticated && !configLoaded"
        @config-loaded="handleConfigLoaded"
      />

      <ConnectionManager
        v-else
        :key="moduleKey"
        :config="connectionConfig"
        @go-back="handleLogout"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import AuthManager from '@/components/AuthManager.vue';
import ConfigManager from '@/components/ConfigManager.vue';
import ConnectionManager, {
  ConnectionConfig,
} from '@/components/ConnectionManager.vue';
import AvatarPopupShell from '@/components/AvatarPopupShell.vue';

const isAvatarPopupMode = (): boolean =>
  new URLSearchParams(window.location.search).get('popup') === 'avatar';

export default defineComponent({
  name: 'VideoV2FrontendApp',
  components: {
    AuthManager,
    ConfigManager,
    ConnectionManager,
    AvatarPopupShell,
  },
  setup() {
    const isAvatarPopup = isAvatarPopupMode();
    const isAuthenticated = ref(false);
    const configLoaded = ref(false);
    const moduleKey = ref(0);
    const connectionConfig = ref<ConnectionConfig>({
      userId: '',
      botId: '',
      accessToken: '',
      voiceId: '',
      videoDeviceId: '',
      suppressStationaryNoise: false,
      suppressNonStationaryNoise: false,
      avatarImage: '/uploads/xingxiaonge6c.gif',
      longestSilenceMs: 3600000,
    });

    const handleAuthSuccess = (): void => {
      isAuthenticated.value = true;
    };

    const handleConfigLoaded = (config: ConnectionConfig): void => {
      connectionConfig.value = config;
      configLoaded.value = true;
    };

    const resetConnectionConfig = (): void => {
      connectionConfig.value = {
        userId: '',
        botId: '',
        accessToken: '',
        voiceId: '',
        videoDeviceId: '',
        suppressStationaryNoise: false,
        suppressNonStationaryNoise: false,
        avatarImage: '/uploads/xingxiaonge6c.gif',
        longestSilenceMs: 3600000,
      };
    };

    const clearClientSession = (): void => {
      sessionStorage.removeItem('username');
      localStorage.removeItem('backendConfig');
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('web_token');
      localStorage.removeItem('pkce_token');
      localStorage.removeItem('device_token');
      localStorage.setItem('user_logged_out', '1');
      document.cookie =
        'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    };

    const exitFullscreen = async (): Promise<void> => {
      const fullscreenDocument = document as Document & {
        webkitFullscreenElement?: Element;
        msFullscreenElement?: Element;
        webkitExitFullscreen?: () => Promise<void> | void;
        msExitFullscreen?: () => Promise<void> | void;
      };

      if (
        !document.fullscreenElement &&
        !fullscreenDocument.webkitFullscreenElement &&
        !fullscreenDocument.msFullscreenElement
      ) {
        return;
      }

      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (fullscreenDocument.webkitExitFullscreen) {
          await fullscreenDocument.webkitExitFullscreen();
        } else if (fullscreenDocument.msExitFullscreen) {
          await fullscreenDocument.msExitFullscreen();
        }
      } catch (error) {
        console.error('Exit fullscreen failed:', error);
      }
    };

    const handleLogout = async (): Promise<void> => {
      await exitFullscreen();

      try {
        await fetch('/api/logout', {
          method: 'POST',
          credentials: 'include',
        });
      } catch (error) {
        console.error('Logout request failed:', error);
      } finally {
        clearClientSession();
        resetConnectionConfig();
        isAuthenticated.value = false;
        configLoaded.value = false;
        moduleKey.value += 1;
      }
    };

    return {
      isAvatarPopup,
      isAuthenticated,
      configLoaded,
      connectionConfig,
      moduleKey,
      handleAuthSuccess,
      handleConfigLoaded,
      handleLogout,
    };
  },
});
</script>

<style scoped>
.module-app {
  display: flex;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  background: var(--bg-gradient-primary);
  overflow: hidden;
}

.avatar-popup-root {
  background: var(--bg-card);
}
</style>
